package er.prateek.crossly.ads.entitlement

import er.prateek.crossly.persistence.EntitlementStore
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.time.Clock

/**
 * Reads the entitlement from a cache at construction so the very first frame already knows
 * whether to reserve banner space, then reconciles against the [PurchaseGateway]
 * asynchronously.
 *
 * The cache is authoritative while offline *up to its own expiry*: a lapsed subscription
 * stops being ad-free without needing a successful network call, while a paying user who
 * opens the app on a plane still gets what they paid for. A failed [refresh] never revokes
 * -- only a gateway that successfully answers "not entitled" does.
 */
class DefaultEntitlementProvider(
    private val store: EntitlementStore,
    private val gateway: PurchaseGateway = NoPurchaseGateway,
    private val clock: Clock = Clock.System,
) : EntitlementProvider {

    private val state = MutableStateFlow(cachedOrNone())

    override val entitlement: StateFlow<Entitlement> = state.asStateFlow()

    override suspend fun refresh() {
        val fresh = runCatching { gateway.queryEntitlement() }.getOrNull() ?: return
        apply(fresh)
    }

    suspend fun purchase(productId: String): PurchaseResult {
        val result = runCatching { gateway.purchase(productId) }
            .getOrElse { PurchaseResult.Failed(it.message ?: "Purchase failed") }
        if (result is PurchaseResult.Success) apply(result.entitlement)
        return result
    }

    suspend fun restore(): Entitlement {
        val restored = runCatching { gateway.restore() }.getOrNull() ?: return state.value
        apply(restored)
        return restored
    }

    suspend fun products(): List<AdFreeProduct> =
        runCatching { gateway.products() }.getOrDefault(emptyList())

    private fun apply(entitlement: Entitlement) {
        store.save(entitlement)
        state.value = entitlement.takeIf { it.isActiveAt(now()) } ?: Entitlement.NONE
    }

    private fun cachedOrNone(): Entitlement =
        store.load().takeIf { it.isActiveAt(now()) } ?: Entitlement.NONE

    private fun now(): Long = clock.now().toEpochMilliseconds()
}
